import React from "react";
import { Building2, ArrowRight, ClipboardList } from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { resolveFormColor, contrastText } from "../form-color.utils";

export interface VendorForm {
	id: string;
	title: string;
	bgColor?: string | null;
}

export interface VendorListItem {
	vendorId: string;
	name: string;
	forms?: VendorForm[];
}

interface VendorListWidgetProps {
	vendors: VendorListItem[];
	onCreateJob: (vendorId: string, formId?: string) => void;
	title: string;
	emptyMessage: string;
	className?: string;
}

export const VendorListWidget: React.FC<VendorListWidgetProps> = ({
	vendors,
	onCreateJob,
	title,
	emptyMessage,
	className,
}) => {
	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 overflow-hidden flex-shrink-0",
				className
			)}
			data-testid="vendor-list-widget"
		>
			<div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50">
				<Building2 className="h-4 w-4 text-gray-500" />
				<h3 className="text-sm font-semibold text-gray-900">{title}</h3>
			</div>

			<div className="border-t border-gray-200" />

			{vendors.length === 0 ? (
				<p className="text-xs text-gray-400 py-3 px-3">{emptyMessage}</p>
			) : (
				<div className="flex flex-col gap-4 p-3">
					{vendors.map((vendor) => (
						<div
							key={vendor.vendorId}
							data-testid={`vendor-card-${vendor.vendorId}`}
						>
							<span className="text-[13px] font-semibold text-gray-800 block mb-2">
								{vendor.name}
							</span>
							{vendor.forms && vendor.forms.length > 0 ? (
								<div className="flex flex-wrap gap-2">
									{vendor.forms.map((form) => {
										const resolved = resolveFormColor(form.bgColor);
										const fg = contrastText(resolved.bg);
										return (
											<button
												key={form.id}
												className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium transition-all hover:opacity-85 hover:shadow-md active:scale-[0.97] cursor-pointer"
												style={{
													backgroundColor: resolved.bg,
													color: fg,
													border: resolved.border ? `1.5px solid ${resolved.border}` : undefined,
												}}
												onClick={() =>
													onCreateJob(vendor.vendorId, form.id)
												}
												aria-label={`${form.title} bei ${vendor.name} öffnen`}
												data-testid={`create-job-${vendor.vendorId}-${form.id}`}
											>
												<ClipboardList
													className="h-3.5 w-3.5"
													style={{ color: fg }}
												/>
												{form.title}
												<ArrowRight
													className="h-3 w-3 ml-0.5 opacity-60"
													style={{ color: fg }}
												/>
											</button>
										);
									})}
								</div>
							) : (
								<Button
									size="sm"
									className="h-9 px-4 text-sm font-medium"
									onClick={() => onCreateJob(vendor.vendorId)}
									aria-label={`Auftrag erstellen bei ${vendor.name}`}
									data-testid={`create-job-${vendor.vendorId}`}
								>
									Auftrag erstellen
									<ArrowRight className="h-3.5 w-3.5 ml-1" />
								</Button>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
